/**
 * FlowHeader - Title, period info, and actions for a flow
 */

import { formatCurrency } from '@/utils/format';
import type { FlowSnapshot, FlowStatus } from '@/services/flows';
import { cn } from '@/utils/cn';

/**
 * Parse ISO date string as local date (not UTC).
 * Avoids timezone issues where new Date(isoString) parses as UTC midnight.
 * Returns current date if isoString is null/undefined/empty.
 */
function parseLocalDate(isoString: string | null | undefined): Date {
  if (!isoString) {
    return new Date();
  }
  const parts = isoString.split('-').map(Number);
  const year = parts[0] ?? 2000;
  const month = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(year, month - 1, day);
}

interface FlowHeaderProps {
  flowName: string | undefined;
  snapshot: FlowSnapshot | undefined;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onExport?: () => void;
  className?: string;
}

/**
 * Format the period type label (e.g., "Monthly", "Quarterly")
 */
function formatPeriodTypeLabel(periodType: string): string {
  switch (periodType) {
    case 'month':
      return 'Monthly';
    case 'quarter':
      return 'Quarterly';
    case 'year':
      return 'Annual';
    case 'ytd':
      return 'Year to Date';
    case 'custom':
      return 'Custom Range';
    default:
      return 'Period';
  }
}

/**
 * Format the date range display string - always shows full range for clarity
 */
function formatDateRange(snapshot: FlowSnapshot): string {
  // Use parseLocalDate to avoid UTC timezone shift
  const startDate = parseLocalDate(snapshot.periodStart);
  const endDate = parseLocalDate(snapshot.periodEnd);

  // Check if same month and year
  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  if (sameMonth && sameYear) {
    // Same month: "Nov 1 - 30, 2024"
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.getDate()}, ${endDate.getFullYear()}`;
  } else if (sameYear) {
    // Same year, different months: "Nov 1 - Dec 31, 2024"
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${endDate.getFullYear()}`;
  } else {
    // Different years: "Dec 15, 2024 - Jan 15, 2025"
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
}

/**
 * Get status badge configuration
 */
function getStatusBadge(status: FlowStatus): { label: string; className: string } | null {
  switch (status) {
    case 'pending':
      return { label: 'Pending', className: 'bg-gray-100 text-gray-800' };
    case 'processing':
      return { label: 'Computing...', className: 'bg-blue-100 text-blue-800' };
    case 'error':
      return { label: 'Error', className: 'bg-red-100 text-red-800' };
    case 'ready':
    default:
      return null;
  }
}

export default function FlowHeader({
  flowName,
  snapshot,
  isFullScreen,
  onToggleFullScreen,
  onRefresh,
  isRefreshing = false,
  onExport,
  className,
}: FlowHeaderProps) {
  const summary = snapshot?.summary;
  const flowStatus = snapshot?.flow?.status;
  const statusBadge = flowStatus ? getStatusBadge(flowStatus) : null;

  return (
    <div className={cn('bg-white border-b border-gray-200', className)}>
      <div className="px-6 py-4">
        {/* Title row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">
              {flowName || 'Flow Visualization'}
            </h1>

            {/* Status badge */}
            {statusBadge && (
              <span
                className={cn(
                  'px-2 py-0.5 text-xs font-medium rounded-full',
                  statusBadge.className
                )}
              >
                {statusBadge.label}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Refresh button */}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className={cn(
                  'p-2 rounded-lg border border-gray-300 hover:bg-gray-50',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                aria-label="Refresh flow data"
              >
                <svg
                  className={cn('h-5 w-5 text-gray-600', isRefreshing && 'animate-spin')}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            )}

            {/* Export PDF button */}
            {onExport && (
              <button
                type="button"
                onClick={onExport}
                className={cn(
                  'p-2 rounded-lg border border-gray-300 hover:bg-gray-50',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                aria-label="Export as PDF"
              >
                <svg
                  className="h-5 w-5 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </button>
            )}

            {/* Full screen toggle */}
            <button
              type="button"
              onClick={onToggleFullScreen}
              className={cn(
                'p-2 rounded-lg border border-gray-300 hover:bg-gray-50',
                'focus:outline-none focus:ring-2 focus:ring-primary-500'
              )}
              aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
            >
              {isFullScreen ? (
                <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Period display - prominent date range */}
        {snapshot && (
          <div className="mb-4 flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
            <svg className="h-5 w-5 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
              <span className="text-sm font-medium text-gray-500">
                {formatPeriodTypeLabel(snapshot.periodType)}:
              </span>
              <span className="text-base font-semibold text-gray-900">
                {formatDateRange(snapshot)}
              </span>
            </div>
          </div>
        )}

        {/* Summary row */}
        {snapshot && (
          <div className="flex flex-wrap items-center gap-4 sm:gap-8">

            {/* Income */}
            {summary && (
              <>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-flow-income" aria-hidden="true" />
                  <span className="text-sm text-gray-600">Income:</span>
                  <span className="font-semibold text-flow-income">
                    {formatCurrency(summary.totalIncome)}
                  </span>
                </div>

                {/* Expenses */}
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-flow-expense" aria-hidden="true" />
                  <span className="text-sm text-gray-600">Expenses:</span>
                  <span className="font-semibold text-flow-expense">
                    {formatCurrency(summary.totalExpenses)}
                  </span>
                </div>

                {/* Net Flow */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Net:</span>
                  <span
                    className={cn(
                      'font-semibold',
                      summary.netFlow >= 0 ? 'text-green-600' : 'text-red-600'
                    )}
                  >
                    {summary.netFlow >= 0 ? '+' : ''}
                    {formatCurrency(summary.netFlow)}
                  </span>
                </div>

                {/* Savings Rate */}
                {summary.savingsRate > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Savings:</span>
                    <span className="font-semibold text-green-600">
                      {(summary.savingsRate * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Computed at timestamp */}
        {snapshot?.computedAt && (
          <div className="mt-2 text-xs text-gray-400">
            Last updated: {new Date(snapshot.computedAt).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
}
